import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePresenceContext } from '../context/PresenceContext';

const EMPTY_ROSTER = { here: [], cells: {}, locks: {} };

/**
 * usePresence(page, options)
 * ------------------------------------------------------------------
 * Joins the shared presence socket for `page` and:
 *   - announces the current user is on `page`
 *   - lets the caller report focus/blur on a cell (an avatar appears on
 *     everyone else's screen) and hold an advisory lock on it
 *   - lets the caller broadcast a save (others patch that one cell - no reload)
 *   - exposes a live `roster` of who is where
 *
 * It carries NO Cetec data - only { user, page, cell, value-on-save }.
 *
 * @param {string} page  room name, e.g. "quotes"
 * @param {object} opts  { onRemoteSave: (evt) => void, onLockDenied: (evt) => void }
 */
export default function usePresence(page, opts = {}) {
    const { onRemoteSave, onLockDenied } = opts;
    const ctx = usePresenceContext();

    const saveCbRef = useRef(onRemoteSave);
    saveCbRef.current = onRemoteSave;
    const denyCbRef = useRef(onLockDenied);
    denyCbRef.current = onLockDenied;

    const { joinPage, leavePage, subscribeSave, subscribeLockDenied } = ctx || {};

    useEffect(() => {
        if (!joinPage || !page) return undefined;
        joinPage(page);
        return () => leavePage(page);
    }, [page, joinPage, leavePage]);

    useEffect(() => {
        if (!subscribeSave) return undefined;
        return subscribeSave((evt) => saveCbRef.current?.(evt));
    }, [subscribeSave]);

    useEffect(() => {
        if (!subscribeLockDenied) return undefined;
        return subscribeLockDenied((evt) => denyCbRef.current?.(evt));
    }, [subscribeLockDenied]);

    const roster = useMemo(() => ctx?.roster || EMPTY_ROSTER, [ctx?.roster]);
    const me = useMemo(
        () => (localStorage.getItem('userEmail') || localStorage.getItem('email') || '').trim().toLowerCase(),
        []
    );

    /** Users (excluding yourself) currently focused on a given cell key. */
    const usersInCell = useCallback(
        (cell) => (roster.cells[cell] || []).filter((u) => u.email !== me),
        [roster, me]
    );

    /** The person holding this cell, or null if it is free or held by you. */
    const lockOwner = useCallback(
        (cell) => {
            const lock = roster.locks[cell];
            if (!lock || lock.email === me) return null;
            return lock;
        },
        [roster, me]
    );

    const isLockedByOther = useCallback((cell) => Boolean(lockOwner(cell)), [lockOwner]);

    const noop = useCallback(() => {}, []);

    return {
        here: roster.here,
        cells: roster.cells,
        locks: roster.locks,
        usersInCell,
        lockOwner,
        isLockedByOther,
        // Focus with a lock request (editors) - reportFocus(cell, { lock: false }) to just show presence.
        reportFocus: ctx?.reportFocus || noop,
        reportBlur: ctx?.reportBlur || noop,
        broadcastSave: ctx?.broadcastSave || noop,
        connected: Boolean(ctx?.connected),
        me,
    };
}
